import type { DayPlanDropRow, DraftGateway, DraftSlot } from '../../../../../drop-generator.types';
import { STRATEGY_CARDS, STRATEGY_LABELS } from '../../../gateways/cards/strategy-copy';
import { sendsWithFor } from '../../../gateways/senders/sender-words';

/** The chip on an automatic gateway: the strategy it follows, in a word or two (user, 19/10/2026: "Auto" said nothing). */
const STRATEGY_CHIP: Record<string, string> = {
  HOLD_UNTIL_FAIL: 'Until fail',
  ALTERNATE: 'Rotates',
  RANDOM: 'Random',
  SPLIT: '% split',
  ROUTE_TEST: 'Test',
};

/** A route test's drops without a gateway (19/10/2026): they take turns, or all use the first gateway — never "until fail". */
const ROUTE_TEST_WORDS: Record<string, { name: string; chip: string; caption: string }> = {
  HOLD_UNTIL_FAIL: { name: 'First gateway', chip: 'First gateway', caption: 'every drop without its own gateway goes through A' },
  ALTERNATE: { name: 'Take turns', chip: 'Takes turns', caption: 'drops without their own gateway go A, B, C, then A again' },
};

/** What "automatic" means for this container: its strategy's name, chip and one-line caption (Gateways tab). */
export function gatewayAutoWords(strategy: string, routeTest = false): { name: string; chip: string; caption: string } {
  if (routeTest) return ROUTE_TEST_WORDS[strategy] ?? ROUTE_TEST_WORDS.ALTERNATE;
  const card = STRATEGY_CARDS.find((c) => c.value === strategy);
  return { name: STRATEGY_LABELS[strategy] ?? 'Automatic', chip: STRATEGY_CHIP[strategy] ?? 'Auto', caption: card?.caption ?? '' };
}

export function slotGatewayLabel(input: {
  slot: DraftSlot;
  gateway: DraftGateway;
  planned?: DayPlanDropRow;
  nameOf: (id: number) => string;
}) {
  const { slot, gateway, planned, nameOf } = input;
  const automatic = slot.routeId === null;
  const lane = automatic
    ? gateway.pool.find((row) => row.id === planned?.gatewayRowId && row.routeId === planned.routeId)
    : (gateway.pool.find((row) => row.id === slot.gatewayRowId) ?? gateway.pool.find((row) => row.routeId === slot.routeId));
  // Not planned today (cut, or not generated): "first gateway" still names A; the other strategies may use any of them.
  const unplanned = gateway.strategy === 'HOLD_UNTIL_FAIL' ? gateway.pool.slice(0, 1) : gateway.pool;
  const lanes = lane ? [lane] : automatic ? unplanned : [];
  const names = [...new Set(lanes.map((row) => nameOf(row.routeId)))];
  // 19/10/2026 — what the gateway sends with, as the Gateways list says it: its IDs, or the AI and how many names.
  const sends = lanes.map((row) => sendsWithFor(gateway.senderDefaults, row));
  const senders = [...new Set(sends.flatMap((x) => x.names))];
  const ai = sends.find((x) => x.ai !== null)?.ai ?? null;
  const one = lanes.length === 1;
  return {
    automatic,
    title: slot.routeId !== null ? nameOf(slot.routeId) : names.join(' / ') || 'Choose gateway',
    detail: [senders.join(' / '), ai !== null ? `AI · ${ai}` : null].filter(Boolean).join(' + ') || 'no sender',
    /** The one gateway this drop names (its letter and colour), else null. */
    laneIndex: one ? gateway.pool.indexOf(lanes[0]) : null,
    /** 20/10/2026 — every gateway it can go through (its letters): the planned one, else what the strategy may pick. */
    indexes: lanes.map((row) => gateway.pool.indexOf(row)),
    /** The route number when it is one gateway. */
    routeId: one ? lanes[0].routeId : slot.routeId,
    /** What that one gateway sends with (null = several, or none known). */
    sends: one ? sends[0] : null,
  };
}

export type SlotGatewayLabel = ReturnType<typeof slotGatewayLabel>;

